import { createStyles, makeStyles, Paper } from "@material-ui/core";
import * as React from "react";

import { baseColors, RelodeTheme, Text, useRelodeApp } from "../..";
import {
    CustomTextJobQualification,
    CustomYesNoJobQualification,
    EducationJobQualification,
    LicenseAnyOfJobQualification,
    LicenseJobQualification,
    SkillJobQualification,
} from "../../domain";
import { JobQualificationsList } from "./jobQualificationsList";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            paper: {
                padding: theme.spacing(2),
            },
            noPadding: {
                padding: theme.spacing(0),
                [theme.breakpoints.down("md")]: {
                    padding: theme.spacing(2),
                },
            },
            list: {
                padding: theme.spacing(0.5, 0, 1, 4),
                margin: 0,
            },
            header: {
                marginBottom: 15,
            },
            preparingContainer: {
                padding: theme.spacing(3.5, 0),
                backgroundColor: baseColors.cool_gray,
                border: "1px solid",
                borderColor: baseColors.warm_gray,
                width: "220px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                alignItems: "center",
                textAlign: "center",
                color: baseColors.light_black,
            },
            preparingWait: {
                display: "flex",
                alignItems: "center",
                gap: "4px",
            }
        }),
    { name: "QualificationView" }
);

interface QualificationViewProps {
    qualifications: {
        educationJobQualifications: EducationJobQualification[];
        skillJobQualifications: SkillJobQualification[];
        skillAnyOfJobQualifications: SkillJobQualification[];
        licenseJobQualifications: LicenseJobQualification[];
        licenseAnyOfJobQualifications: LicenseAnyOfJobQualification[];
        customYesNoJobQualifications: CustomYesNoJobQualification[];
        customTextJobQualifications: CustomTextJobQualification[];
    };
    addJobPreviewMode?: boolean;
}

export const JobQualificationsView = (props: QualificationViewProps) => {
    const { qualifications, addJobPreviewMode } = props;
    const classes = useStyles({});
    const {
        state: {
            config: { appName },
        },
    } = useRelodeApp();

    let hasAnyRequired = false;
    let hasAnyPreferred = false;

    const requiredEducation = [];
    const preferredEducation = [];

    for (const qual of qualifications?.educationJobQualifications ?? []) {
        if (qual.isRequired) {
            requiredEducation.push(qual);
            hasAnyRequired = true;
        } else {
            preferredEducation.push(qual);
            hasAnyPreferred = true;
        }
    }

    const requiredSkill = [];
    const preferredSkill = [];

    for (const skill of qualifications?.skillJobQualifications ?? []) {
        if (skill.isRequired) {
            requiredSkill.push(skill);
            hasAnyRequired = true;
        } else {
            preferredSkill.push(skill);
            hasAnyPreferred = true;
        }
    }

    const requiredSkillAnyOf = [];
    const preferredSkillAnyOf = [];

    for (const skillAnyOf of qualifications?.skillAnyOfJobQualifications ??
        []) {
        if (skillAnyOf.isRequired) {
            requiredSkillAnyOf.push(skillAnyOf);
            hasAnyRequired = true;
        } else {
            preferredSkillAnyOf.push(skillAnyOf);
            hasAnyPreferred = true;
        }
    }
    const preferredLicense = [];
    const requiredLicense = [];

    for (const license of qualifications?.licenseJobQualifications ?? []) {
        if (license.isRequired) {
            requiredLicense.push(license);
            hasAnyRequired = true;
        } else {
            preferredLicense.push(license);
            hasAnyPreferred = true;
        }
    }

    const preferredLicenseAnyOf = [];
    const requiredLicenseAnyOf = [];

    for (const licenseAnyOf of qualifications?.licenseAnyOfJobQualifications ??
        []) {
        if (licenseAnyOf.isRequired) {
            requiredLicenseAnyOf.push(licenseAnyOf);
            hasAnyRequired = true;
        } else {
            preferredLicenseAnyOf.push(licenseAnyOf);
            hasAnyPreferred = true;
        }
    }

    const requiredCustomYesNo = [];
    const preferredCustomYesNo = [];

    for (const customYesNo of qualifications?.customYesNoJobQualifications ??
        []) {
        if (customYesNo.isRequired) {
            requiredCustomYesNo.push(customYesNo);
            hasAnyRequired = true;
        } else {
            preferredCustomYesNo.push(customYesNo);
            hasAnyPreferred = true;
        }
    }

    const requiredCustomText = [];
    const preferredCustomText = [];

    for (const customText of qualifications?.customTextJobQualifications ??
        []) {
        if (customText.isRequired) {
            requiredCustomText.push(customText);
            hasAnyRequired = true;
        } else {
            preferredCustomText.push(customText);
            hasAnyPreferred = true;
        }
    }

    // NOTE: cannot use getUserFromStorage as this control used in /public/ anonymous scenarios, too!
    const isClient = appName === "client";
    const isAdmin = appName === "admin";

    // within each set of specific qualifications created a required and preferred list:

    return (
        <Paper
            elevation={isClient || isAdmin && !addJobPreviewMode ? 0 : 1}
            className={isClient || isAdmin && !addJobPreviewMode ? classes.noPadding : classes.paper}
        >
            {isAdmin && !addJobPreviewMode ? null : <Text variant="h2" gutterBottom>
                qualifications
            </Text>}

            {!hasAnyRequired && !hasAnyPreferred ? (
                <Text variant="body1">no qualifications have been added</Text>
            ) : null}

            {hasAnyRequired && (
                <JobQualificationsList
                    title="required"
                    jobQualifications={{
                        educationJobQualifications: requiredEducation,
                        skillJobQualifications: requiredSkill,
                        skillAnyOfJobQualifications: requiredSkillAnyOf,
                        licenseJobQualifications: requiredLicense,
                        licenseAnyOfJobQualifications: requiredLicenseAnyOf,
                        customYesNoJobQualifications: requiredCustomYesNo,
                        customTextJobQualifications: requiredCustomText
                    }}
                />
            )}
            {hasAnyPreferred && (
                <JobQualificationsList
                    title="preferred"
                    jobQualifications={{
                        educationJobQualifications: preferredEducation,
                        skillJobQualifications: preferredSkill,
                        skillAnyOfJobQualifications: preferredSkillAnyOf,
                        licenseJobQualifications: preferredLicense,
                        licenseAnyOfJobQualifications: preferredLicenseAnyOf,
                        customYesNoJobQualifications: preferredCustomYesNo,
                        customTextJobQualifications: preferredCustomText
                    }}
                />
            )}
        </Paper>
    );
};
